/* eslint-disable */
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { colors, CommonModal, CustomPagination, FunctionsEnum } from '@watter/shared-components';
import { installationEnums } from '@watter/shared-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AntSwitch, CommonTable } from '../../components/common-styles';
import { CustomSlider } from '../../components/slider/slider';
import { usePermissions } from '../../custom_hooks/permissions';
import { UPDATE_INSTALLATION } from '../../graphql/mutations';
import { GET_MANAGE_WORK_ORDERS } from '../../graphql/queries';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { formatToUSDate } from '../../utils/helper';
import { AddEditWorkOrder } from './add-edit-work-order';

declare global {
    interface Window {
        handleAddWorkOrder?: () => void;
    }
}
interface WorkOrderRow {
    id: number;
    installer_name: string;
    installer_user_id?: string;
    customer_id?: string | null;
    customer_name?: number | null;
    address_id?: string | null;
    status: string;
    installation_started_at?: string;
    installation_date?: string;
    machine_serial_id?: string | null;
    installation_ended_at?: string;
    agency_id?: string;
    user_status?: number;
}

interface WorkOrderData {
    id: number;
    installer_name: string;
    installer_user_id?: string;
    customer_id?: string | null;
    customer_name?: number | null;
    address_id?: string | null;
    status: number;
    installation_started_at?: string;
    installation_date?: string;
    machine_serial_id?: string | null;
    installation_ended_at?: string;
    agency_id?: string;
    user_status?: number;
    installation_status?: number;
}

export const ManageWorkOrder: React.FC = () => {
    const dispatch = useDispatch();
    const installationStatusMap = React.useMemo<Record<number, string>>(
        () => ({
            [installationEnums.InstallationStatus.initiated]: 'Initiated',
            [installationEnums.InstallationStatus.inprogress]: 'In progress',
            [installationEnums.InstallationStatus.inactive]: 'Inactive',
            [installationEnums.InstallationStatus.completed]: 'Completed',
        }),
        [],
    );
    const statusColors: Record<string, string> = {
        Initiated: colors.pending,
        Inactive: colors.statusGray,
        Completed: colors.success,
        'In progress': colors.statusDarkBlue,
    };
    const [open, setOpen] = React.useState(false);
    const [manageWorkOrdersData, setManageWorkOrdersData] = useState<WorkOrderData[]>([]);
    const [selectedInstaller, setSelectedInstaller] = useState<WorkOrderRow | null>(null);
    const [newStatus, setNewStatus] = useState<number | undefined | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [totalCount, setTotalRecordCount] = React.useState(0);
    const rowsPerPage = 10;
    const [page, setCurrentPage] = React.useState(1);
    const tableRef = useRef<HTMLInputElement>(null);
    const { canEdit } = usePermissions();
    const getStatusText = React.useCallback(
        (status: number) => installationStatusMap[status] || 'Initiated',
        [installationStatusMap],
    );
    const handleEditClick = (installer: React.SetStateAction<WorkOrderRow | null>, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedInstaller(installer);
        setOpen(true);
        setIsEdit(true);
    };
    const handleStatusToggle = (row: WorkOrderRow) => {
        setSelectedInstaller(row);
        setNewStatus(
            row.status === 'Inactive'
                ? installationEnums.InstallationStatus.inactive
                : installationEnums.InstallationStatus.initiated,
        );
        setModalOpen(true);
    };
    const handleAddWorkOrder = () => {
        setOpen(true);
    };
    useEffect(() => {
        window.handleAddWorkOrder = handleAddWorkOrder;

        return () => {
            delete window.handleAddWorkOrder;
        };
    }, []);

    const fetchMangeWorkOrderData = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query<{
                getAdminInstallationsList: {
                    data: WorkOrderData[];
                    total_count: number;
                };
            }>({
                query: GET_MANAGE_WORK_ORDERS,
                variables: {
                    limit: rowsPerPage,
                    page,
                },
                fetchPolicy: 'network-only',
            });

            if (data?.getAdminInstallationsList?.data) {
                const formattedRows: any = data.getAdminInstallationsList.data.map(installer => ({
                    display_id: installer.id ? `WO-${installer.id}` : '-',
                    id: installer.id,
                    customer_name: installer.customer_name || '-',
                    installer_name: installer.installer_name || '-',
                    customer_id: installer.customer_id,
                    installation_date: installer?.installation_date
                        ? formatToUSDate(installer?.installation_date)
                        : '-',
                    user_status:
                        installer.status === installationEnums.InstallationStatus.inactive ? 'Inactive' : 'Active',
                    machine_serial_id: installer.machine_serial_id,
                    installer_user_id: installer.installer_user_id,
                    address_id: installer.address_id,
                    agency_id: installer.agency_id,
                    status: getStatusText(installer.status),
                    display_date: installer?.installation_date,
                    installation_status: installer.status,
                }));
                setManageWorkOrdersData(formattedRows);
                setTotalRecordCount(data?.getAdminInstallationsList?.total_count);
            } else {
                console.warn('No data found in API response');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, page, rowsPerPage, getStatusText]);

    const handleStatusToggleApi = async (
        customer_id: string | null | undefined,
        status: number | undefined,
        id: number,
        installer_user_id: string | null | undefined,
    ) => {
        try {
            dispatch(showLoader());

            const input = {
                status: status === 1 ? installationEnums.InstallationStatus.inactive : 1,
                id,
                customer_id,
                installer_user_id,
            };
            const { data } = await client.mutate({
                mutation: UPDATE_INSTALLATION,
                variables: { input },
            });

            await fetchMangeWorkOrderData();

            dispatch(
                openToast({
                    show: true,
                    message: data?.updateInstallation?.message,
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
        } catch (err) {
            console.error('Error while changing the status:', err);
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleConfirmStatusChange = async () => {
        if (!selectedInstaller || newStatus === null) return;

        await handleStatusToggleApi(
            selectedInstaller.customer_id,
            newStatus,
            selectedInstaller.id,
            selectedInstaller.installer_user_id,
        );

        setModalOpen(false);
        setSelectedInstaller(null);
        setNewStatus(null);
    };

    const columns: GridColDef[] = [
        { field: 'display_id', headerName: 'ID', flex: 0.5 },
        { field: 'customer_name', headerName: 'Customer Name', flex: 1.2 },
        { field: 'installer_name', headerName: 'Installer Name', flex: 1.2 },
        { field: 'installation_date', headerName: 'Scheduled Installation Date', flex: 1 },
        {
            field: 'status',
            headerName: 'Installation Status',
            flex: 0.7,
            headerAlign: 'center',
            align: 'center',
            renderCell: params => (
                <Stack direction="row" height="100%" alignItems="center" justifyContent="center">
                    <Typography
                        variant="subtitle1"
                        textAlign="center"
                        className="statusText"
                        bgcolor={statusColors[params.value] || colors.pending}
                    >
                        {params.value}
                    </Typography>
                </Stack>
            ),
        },
        ...(canEdit(FunctionsEnum.MANAGE_WORKORDERS)
            ? [
                  {
                      field: 'user_status',
                      headerName: 'Status',
                      sortable: false,
                      flex: 0.4,
                      headerAlign: 'center' as const,
                      align: 'center' as const,
                      cellClassName: canEdit(FunctionsEnum.MANAGE_WORKORDERS) ? 'cstRemovePointer' : '',
                      renderCell: (params: GridRenderCellParams) => {
                          const isActive = params.value !== 'Inactive';
                          return (
                              <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                                  <AntSwitch
                                      disabled={
                                          params.row.installation_status ===
                                              installationEnums.InstallationStatus.completed ||
                                          !canEdit(FunctionsEnum.MANAGE_WORKORDERS)
                                      }
                                      checked={isActive}
                                      onClick={e => {
                                          e.stopPropagation();
                                          handleStatusToggle(params.row);
                                      }}
                                      sx={{
                                          '&.Mui-disabled': {
                                              opacity: 0.5,
                                              cursor: 'not-allowed',
                                          },
                                      }}
                                  />
                              </Box>
                          );
                      },
                  },
              ]
            : []),
        ...(canEdit(FunctionsEnum.MANAGE_WORKORDERS)
            ? [
                  {
                      field: 'actions',
                      headerName: 'Actions',
                      sortable: false,
                      flex: 0.4,
                      headerAlign: 'right' as const,
                      align: 'right' as const,
                      cellClassName: canEdit(FunctionsEnum.MANAGE_WORKORDERS) ? 'cstRemovePointer' : '',
                      renderCell: (params: GridRenderCellParams) => (
                          <Box display="flex" justifyContent="end">
                              <IconButton
                                  disabled={
                                      params.row.installation_status ===
                                          installationEnums.InstallationStatus.completed ||
                                      !canEdit(FunctionsEnum.MANAGE_WORKORDERS)
                                  }
                                  onClick={e => handleEditClick(params.row, e)}
                                  sx={{
                                      '&.Mui-disabled': {
                                          opacity: 0.5,
                                          cursor: 'not-allowed',
                                      },
                                  }}
                              >
                                  {' '}
                                  <EditOutlinedIcon />
                              </IconButton>
                          </Box>
                      ),
                  },
              ]
            : []),
    ];

    React.useEffect(() => {
        setManageWorkOrdersData([]);
        fetchMangeWorkOrderData();
        if (tableRef.current) {
            tableRef.current.scrollIntoView();
        }
    }, [page, fetchMangeWorkOrderData]);

    return (
        <CommonTable height="calc(100vh - 204px)">
            <Paper sx={{ width: '100%' }}>
                <DataGrid
                    rows={manageWorkOrdersData}
                    columns={columns}
                    disableColumnMenu
                    disableColumnSorting
                    hideFooter
                    ref={tableRef}
                    getRowClassName={params =>
                        params.row.installation_status === installationEnums.InstallationStatus.completed
                            ? 'cstDisablePointer'
                            : ''
                    }
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'initial !important',
                        },
                    }}
                />
                {totalCount > 0 && (
                    <CustomPagination
                        totalCount={totalCount}
                        rowsPerPage={rowsPerPage}
                        currentPage={page}
                        onPageChange={setCurrentPage}
                    />
                )}
                <CustomSlider
                    sliderOpen={open}
                    sliderClose={() => {
                        setOpen(false);
                        setIsEdit(false);
                    }}
                    drawerList={
                        <AddEditWorkOrder
                            onClose={() => {
                                setOpen(false);
                                setIsEdit(false);
                            }}
                            installer={selectedInstaller}
                            fetchMangeInstallerData={fetchMangeWorkOrderData}
                            isEdit={isEdit}
                        />
                    }
                />
            </Paper>
            <CommonModal
                modalOpen={modalOpen}
                header={`Do you want to ${newStatus === installationEnums.InstallationStatus.inactive ? 'activate' : 'inactivate'} this work order?`}
                cancelBtnName="Cancel"
                AcceptBtnName="Yes"
                closePopup={() => setModalOpen(false)}
                acceptBtn={handleConfirmStatusChange}
            />
        </CommonTable>
    );
};
